import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { logError } from "../utils/logger";

const COMMENTS_PER_PAGE = 10;

export function useComments(noteId, highlightCommentId = null) {
  // Get current user from auth context
  const { user } = useAuth();

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [posting, setPosting] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);
  const [isAutoLoadingForHighlight, setIsAutoLoadingForHighlight] =
    useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Fetch initial comments
  useEffect(() => {
    let cancelled = false;

    async function fetchInitialComments() {
      try {
        setLoading(true);
        setError(null);

        const response = await api.getComments(noteId, {
          offset: 0,
          limit: COMMENTS_PER_PAGE,
        });

        if (!cancelled) {
          setComments(response.data);
          setHasMore(response.hasMore);
          setOffset(COMMENTS_PER_PAGE);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load comments");
          logError("Failed to fetch comments:", err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchInitialComments();

    return () => {
      cancelled = true;
    };
  }, [noteId, refetchTrigger]);

  // Auto-load pages in parallel batches to find highlighted comment
  useEffect(() => {
    if (
      !highlightCommentId ||
      !hasMore ||
      loading ||
      isAutoLoadingForHighlight
    ) {
      return;
    }

    // Check if the highlighted comment is already in the loaded comments
    const commentExists = comments.some((c) => c.id === highlightCommentId);

    if (commentExists) {
      // Comment found, no need to load more
      return;
    }

    // Comment not found and there are more pages, load next batch (3 pages in parallel)
    setIsAutoLoadingForHighlight(true);

    async function autoLoadBatch() {
      try {
        const BATCH_SIZE = 3; // Load 3 pages in parallel
        const batchRequests = [];

        // Create requests for the next 3 pages
        for (let i = 0; i < BATCH_SIZE; i++) {
          batchRequests.push(
            api.getComments(noteId, {
              offset: offset + i * COMMENTS_PER_PAGE,
              limit: COMMENTS_PER_PAGE,
            }),
          );
        }

        // Load all pages in parallel
        const results = await Promise.all(batchRequests);

        // Combine all results
        const allNewComments = results.flatMap((r) => r.data);
        const lastResult = results[results.length - 1];

        setComments((prev) => [...prev, ...allNewComments]);
        setHasMore(lastResult.hasMore);
        setOffset((prev) => prev + BATCH_SIZE * COMMENTS_PER_PAGE);
      } catch (err) {
        logError("Failed to auto-load comments for highlight:", err);
      } finally {
        setIsAutoLoadingForHighlight(false);
      }
    }

    autoLoadBatch();
  }, [
    highlightCommentId,
    comments,
    hasMore,
    loading,
    offset,
    isAutoLoadingForHighlight,
    noteId,
  ]);

  // Refetch Comment
  const refetch = useCallback(() => setRefetchTrigger((t) => t + 1), []);

  // Load more comments
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      setError(null);

      const response = await api.getComments(noteId, {
        offset,
        limit: COMMENTS_PER_PAGE,
      });

      setComments((prev) => [...prev, ...response.data]);
      setHasMore(response.hasMore);
      setOffset((prev) => prev + COMMENTS_PER_PAGE);
    } catch (err) {
      setError(err.message || "Failed to load more comments");
      logError("Failed to load more comments:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [noteId, offset, hasMore, loadingMore]);

  // Add a new comment
  const addComment = useCallback(
    async (text) => {
      if (!text.trim()) return;

      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      try {
        setPosting(true);
        setError(null);

        // Pass userId to API (mirrors real backend behavior)
        const response = await api.addComment(noteId, { text });

        // Add the new comment to the beginning of the list
        setComments((prev) => [response.data, ...prev]);

        // Return both the comment and updated stats
        return {
          comment: response.data,
          stats: response.stats,
        };
      } catch (err) {
        setError(err.message || "Failed to post comment");
        logError("Failed to add comment:", err);
        throw err;
      } finally {
        setPosting(false);
      }
    },
    [noteId, user],
  );

  // Delete a comment
  const deleteComment = useCallback(
    async (commentId) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      try {
        setError(null);

        // Optimistically remove from UI
        setComments((prev) => prev.filter((c) => c.id !== commentId));

        // Call API to delete
        await api.deleteComment(noteId, commentId);
      } catch (err) {
        setError(err.message || "Failed to delete comment");
        logError("Failed to delete comment:", err);
        throw err;
      }
    },
    [noteId, user],
  );

  return {
    comments,
    loading,
    loadingMore,
    posting,
    hasMore,
    error,
    loadMore,
    addComment,
    deleteComment,
    refetch,
  };
}
